import React from "react";
import { Link } from "react-router-dom";
import { Box, Button, HStack, Text } from "components/ui";

export interface SuggestionBannerProps {
  /** Icon rendered at the start of the banner. */
  icon: React.ReactNode;
  /** Already-translated message. Pluralisation belongs to the caller's i18n key. */
  message: string;
  /** Already-translated label for the inline link. */
  linkLabel: string;
  /** Where the link goes — the page where these suggestions can be acted on. */
  linkTo: string;
  /** Chakra colour palette shade for the banner background. */
  bg: string;
  borderColor: string;
  /** Accessible label for the close button. */
  dismissLabel: string;
  /**
   * Called by BOTH the close button and the review link — following the link counts as having seen
   * the banner, so it should not reappear on the next login.
   */
  onDismiss: () => void;
}

/**
 * The persistent strip above the admin content that surfaces pending AI suggestions.
 *
 * Extracted from the inline masking-only block in the admin layout so a second suggestion type did
 * not mean a second copy of the same markup. Dismissing hides it for the session only — the
 * suggestions themselves are untouched.
 */
export const SuggestionBanner: React.FC<SuggestionBannerProps> = ({
  icon,
  message,
  linkLabel,
  linkTo,
  bg,
  borderColor,
  dismissLabel,
  onDismiss,
}) => (
  <Box
    px={6}
    h={16}
    bg={bg}
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    flexShrink={0}
    borderBottom="1px solid"
    borderColor={borderColor}
  >
    <HStack gap={3}>
      {icon}
      <Text color="white" fontWeight="medium" fontSize="sm">
        {message}{" "}
        <Link
          to={linkTo}
          onClick={onDismiss}
          style={{
            color: "white",
            textDecoration: "underline",
            fontWeight: "bold",
          }}
        >
          {linkLabel}
        </Link>
      </Text>
    </HStack>
    <Button
      size="xs"
      intent="ghost"
      color="white"
      minW="auto"
      px={1}
      onClick={onDismiss}
      aria-label={dismissLabel}
    >
      ✕
    </Button>
  </Box>
);
